import { inject, Injectable } from '@angular/core'
import { nanoidBrowser } from '@naturalcycles/js-lib'
import {
  CartState,
  cookieService,
  ExperimentalPrice,
  ProductType,
  SocialMediaMarketingService,
  Utms,
  utmService,
} from '@naturalcycles/shared'
import { AddShoppersService } from '@src/app/core/services/add-shoppers.service.js'
import { MixpanelService } from '@src/app/core/services/analytics/mixpanel.service.js'
import { SignalStore } from '@src/app/core/store/signalStore.js'
import { parseCookieConsentString } from '@src/app/core/util/analytics.util.js'
import { PageViewData } from '@src/app/shared/typings/analytics.js'
import { api } from '../api.service.js'
import { SentryService } from '../sentry.service.js'

export const socialMediaMarketingService = new SocialMediaMarketingService()

@Injectable({ providedIn: 'root' })
export class AnalyticsService {
  private store = inject(SignalStore)
  private mixpanelService = inject(MixpanelService)
  private addShoppersService = inject(AddShoppersService)
  private sentryService = inject(SentryService)

  getAllUtms(): Utms {
    try {
      const utms = utmService.refresh()
      return utms
    } catch (err) {
      console.log(cookieService.getCookie('utms'), err)
      this.sentryService.captureException(new Error('Issue while refreshing utms'))
      return {}
    }
  }

  async pageView(url: string, data: PageViewData): Promise<void> {
    const id = nanoidBrowser()
    const { consentCookie } = data

    const marketingConsent = parseCookieConsentString(consentCookie)?.marketing

    socialMediaMarketingService.pageView({ id })

    await Promise.all([
      this.mixpanelService.trackView(url, data),
      api.post('analyticsHooks/pageview', { json: { id, url, marketingConsent } }),
    ])
  }

  async purchase(cart: CartState): Promise<void> {
    const id = nanoidBrowser()
    if (cart.discount?.referrer?.name) {
      delete cart.discount.referrer.name
    }

    const { items, currency, netTotalAmount } = cart
    const product = items.find(i => i.type === ProductType.SUBSCRIPTION)!.key
    const { email, shippingAddress } = this.store.$account()
    const amount = ExperimentalPrice.centsToUnits(netTotalAmount)
    void socialMediaMarketingService.purchase({
      id,
      amount,
      currency,
      products: items,
      email,
      phone: shippingAddress?.phoneNumber?.toString(),
    })

    await Promise.all([
      api.post('analyticsHooks/purchase', {
        json: {
          id,
          product,
          currency,
          amount,
        },
      }),
      this.addShoppersService.trackConversion({
        order_id: this.store.$account().externalPersonalId,
        value: `${amount}`,
        currency,
      }),
    ])
  }

  trackEvent(eventId: string, opt?: any): void {
    this.mixpanelService.trackEvent(eventId, opt)
  }
}
